import { Box, Button, CircularProgress, Modal, Paper, Slide, Stack, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { ID } from "@/data/types/Basic";
import { showStoreStockBtnSX, showStoreStockMdlSX, storeSearchPanelSX, storeStockAvailabilitySX, storeStockMdlContentSX, storeStockMdlTitleSX } from "./styles/container";
import { Switch } from "@/utils/switch";
import CloseIcon from '@mui/icons-material/Close';
import { ProductCard } from "@/components/blocks/ProductCard";
import { useSettings } from "@/data/Settings";
import { LatLng, StoreDetails, StoresByIndex } from "@/data/types/Store";
import { useSapStoreStock } from "@/data/Content/SapInventory";
import { useLocalization } from "@/data/Localization";
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { StockAvailabilityColor, getStoreStockAvailabilityText, getStoreURL } from "@/data/utils/StoreStock";
import { Linkable } from "@/components/blocks/Linkable";
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { StoreStockAutoComplete } from "@/components/blocks/StoreStockAutoComplete";
import { useStoreStockByLocation } from "@/data/Content/StoreLocator";
import { useProduct } from "@/data/Content/Product";
import { globalBreak } from "@/styles/break";

export const StoreStockRv: FC<{ id: ID }> = ({ id }) => {

    const { settings } = useSettings();
    const autoCompleteOptions = {
        radius:settings.userData.StoreLocator_Radius,
        radiusUOM:settings.userData.StoreLocator_RadiusUOM,
        maxItems:settings.userData.StoreLocatorStoreStockPDP_MaxItems??"25",
    }
    const [open, setOpen] = useState(false);
    const [showMoreStores, setShowMoreStores] = useState(false);
    
    const [storesIndex, setStoresIndex] = useState(0);
    const [storesByIndex, setStoresByIndex] = useState<StoresByIndex>({});
    const [storeURLs, setStoreURLs] = useState<{[key:string]:string}>({});
    const [selectedStores, setSelectedStores] = useState<StoreDetails[]>([]);
    const [storeStock, setStoreStock] = useState<{ [key: string]: string }>({});
    const [stores, setStores] = useState<StoreDetails[]>([]);
    const [latLng, setLatLng] = useState<LatLng>({} as LatLng);
    
    const [storeNumber, setStoreNumber] = useState("");
    
    const { product } = useProduct({ id: id.toString() });
    
    const { sapStoreStock, isLoading:isSapStoreStockLoading, isSapStockStatusAvailable, setSapStockStatusAvailable } = useSapStoreStock(storeNumber, id.toString());

    const storeStockNLS = useLocalization('StoreStock');
    const storeLocatorRoute = useLocalization('Routes').StoreLocator.route.t();
    const stockAvailabilityText = getStoreStockAvailabilityText(storeStockNLS.InvMsg);
    
    const storeStockStoreCountTODisplay = settings.userData.StoreStockStoreCountTODisplay??'5';

    const handleModal = useCallback(
        (action?: string) => () =>
            {
                setOpen((open) =>
                    Switch(action)
                        .case('open', () => true)
                        .case('close', () => {
                            handleModalCLose();
                            return false;
                        })
                        .defaultTo(() => !open)
                );
                if(action==='close'){
                    setSapStockStatusAvailable(true);
                }
            },
        []
    );

    const handleModalCLose = useCallback(() => {
        setStoreStock({});
        setSelectedStores([]);
        setStoresIndex(0);
    }, []);

    const { stores:tempStores } = useStoreStockByLocation(latLng, autoCompleteOptions);

    useEffect(()=>{
        setSelectedStores([]);
    },[latLng]);

    useEffect(()=>{
        if(tempStores?.length>0){
            setStores(tempStores);
            setLatLng({} as LatLng);
        }
    },[tempStores])

    useEffect(() => {
        if (stores && stores.length > 0) {
            let tempStoresByIndex: StoresByIndex = {};
            const tempStores = [...stores];
            let index: number = 1;
            while (tempStores.length > 0) {
                tempStoresByIndex[index] = tempStores.splice(0, parseInt(storeStockStoreCountTODisplay));
                index++;
            }
            setStoresIndex(1);
            setStoresByIndex(tempStoresByIndex);
            //setLatLng({} as LatLng);
            let tempStoresURL: { [key: string]: string } = {};
            [...stores].forEach((store: StoreDetails) => {
                tempStoresURL[store.physicalStoreName] =  getStoreURL(store, storeLocatorRoute);
            });
            setStoreURLs(tempStoresURL);
        }
    }, [stores]);

    useEffect(() => {
        const tempStoresByIndex = { ...storesByIndex };
        const tempStores = tempStoresByIndex[storesIndex];
        if (tempStores) setSelectedStores([...selectedStores, ...tempStores]);
        const tempStoreNumber = tempStores && tempStores.map(store => store.physicalStoreName).join(",");
        setStoreNumber(tempStoreNumber);
    }, [storesIndex, storesByIndex]);

    useEffect(() => {
        if (sapStoreStock?.inventory && sapStoreStock?.inventory.length > 0) {
            let tempStoreStock: { [key: string]: string } = {};
            sapStoreStock?.inventory.forEach((sapStore: any) => {
                tempStoreStock[sapStore.storeNumber] = sapStore?.partNumbers?.at(0)?.available??"OutOfStock";
            })
            setStoreStock({ ...storeStock, ...tempStoreStock });
        }
        
        if(storeNumber && !isSapStoreStockLoading)setStoreNumber("");// Setting store number to empty after reading the store stock response
    }, [sapStoreStock,isSapStoreStockLoading])

    useEffect(() => {
        const maxI: number = Math.max(...Object.keys(storesByIndex).map(Number));
        setShowMoreStores(storesIndex > 0 && storesIndex < maxI);
    }, [storesIndex, storesByIndex]);
    
    return (
        <Box>
            <Button variant="semiConversion" sx={showStoreStockBtnSX} onClick={() => setOpen(true)}>
                {storeStockNLS.ViewStoreInventory.t()}
            </Button>
            <Modal
                open={open}
                onClose={handleModal('close')}
                hideBackdrop={false}
            >
                <Slide in={open} direction="left">
                    <Paper sx={showStoreStockMdlSX} elevation={10}>
                        <Stack id="store_stock_modal_title" sx={storeStockMdlTitleSX}>
                            <Typography variant="h4"  >{storeStockNLS.StoreStockHeaderLabel.t()}</Typography>
                            <Button variant="textButton" onClick={handleModal('close')}>
                                <CloseIcon sx={{ cursor: 'pointer' }} />
                            </Button>
                        </Stack>
                        <Box id="store_stock_modal_content" sx={storeStockMdlContentSX}>
                            {product ?
                                <Stack justifyContent="center" direction="row">
                                    <Box sx={{maxWidth:globalBreak({mobile:'11em',desktop:'13.5em'})}}>
                                        <ProductCard product={product} styles={{cardImageSx:{width:'auto'}}} />
                                    </Box>
                                </Stack>
                            :null}
                            <Box sx={storeSearchPanelSX}>
                                <Typography variant="strong" mb={1.5} component="p">{storeStockNLS.FindStore.t()}</Typography>
                                <StoreStockAutoComplete setLatLngStoreStock={setLatLng} /> 
                                
                                <Box pb={2} pt={0}>
                                    {isSapStockStatusAvailable && selectedStores && selectedStores.map((store) =>
                                        <Box sx={{ borderBottom: '1px solid #E2E2E2' }} pb={2} mt={2}>
                                            <Linkable href={storeURLs[store.physicalStoreName]}>
                                                <Typography variant="h3" pb={0.5}>{store.storeName}&#40;{parseFloat(store.distance).toFixed(2)}km&#41;</Typography>
                                            </Linkable>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" py={1} >
                                                <Box>
                                                    <Typography>{store.displayAddress}</Typography>
                                                    <Typography>{store.city}</Typography>
                                                </Box>
                                                {storeStock[store.physicalStoreName] ?
                                                    <Stack sx={storeStockAvailabilitySX}>
                                                        <StopRoundedIcon sx={{ color: StockAvailabilityColor[storeStock[store.physicalStoreName]], fontSize: 24 }} />
                                                        <Typography fontSize={14} fontWeight={700} color={StockAvailabilityColor[storeStock[store.physicalStoreName]]}>
                                                            {stockAvailabilityText[storeStock[store.physicalStoreName]]}
                                                        </Typography>
                                                    </Stack>
                                                    :
                                                    <CircularProgress sx={{color:'#464646'}} size={30} />
                                                }
                                            </Stack>
                                            <Linkable href={storeURLs[store.physicalStoreName]}>
                                                <Stack direction="row" alignItems="center" pt={1} columnGap={0.5}>
                                                    <Typography>{storeStockNLS.ViewStoreOpeningHours.t()}</Typography>
                                                    <ArrowForwardRoundedIcon />
                                                </Stack>
                                            </Linkable>
                                        </Box>
                                    )}
                                     {!isSapStockStatusAvailable ? <Typography color="#d32f2f" p={1}>{storeStockNLS.Error.SAPStockNotAvailable.t()}</Typography> : null}
                                </Box>
                                {isSapStockStatusAvailable && showMoreStores ?
                                    <Button variant="clickThrough" fullWidth onClick={() => setStoresIndex(storesIndex + 1)} disabled={isSapStoreStockLoading}>
                                        {storeStockNLS.ShowMore.t()}
                                    </Button>
                                :null}
                            </Box>
                        </Box>
                    </Paper>
                </Slide>
            </Modal>
        </Box>
    );
}